import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Navigation from '../../Components/Navigation';
import ButtonNext from '../../Components/ButtonNext';
// import PhoneInput from 'react-phone-number-input/input';
import AppHeaderLogo from '../../Images/app-header_logo_2x.png';
import DemandPreview from '../../Images/demand-preview.png';
import FeedPreview from '../../Images/feed-preview.png';
import { getLandingVariant, DEFAULT_LANDING_VARIANT, FRIEND_LANDING_VARIANT } from '../../config/landingVariants';
import { trackGA4 } from '../../ga4';

// Varianter som visar en produktpreview (istället för explainer/aboveCta).
// Lägg till en nyckel här för att aktivera preview-layouten för en variant.
const VARIANT_PREVIEWS = {
  seller_demand_v1: { image: DemandPreview, alt: 'Förhandsvisning av efterfrågan i Houtive-appen' },
  buyer_demand_v1: { image: FeedPreview, alt: 'Förhandsvisning av bostäder i Houtive-appen' },
};

const WhoInvitedMe = ({
  inviterPhoneNumber,
  setInviterPhoneNumber,
  inviteLinkError,
  inviteCode,
  setInviteCode,
  mode,
  setMode,
  LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER,
  LOCAL_STORAGE_KEY_INVITE_CODE,
  LOCAL_STORAGE_KEY_MODE,
  SESSION_STORAGE_KEY_ATTRIBUTION,
  SESSION_STORAGE_KEY_LANDING_VARIANT,
  inviterResolvedFromParams,
  setInviterResolvedFromParams,
  codeResolvedFromParams,
  setCodeResolvedFromParams
}) => {
  const navigate = useNavigate();
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [error, setError] = useState(false);
  // Landningsvariant (endast för /i/-koder som kommer via URL)
  const [variantKey, setVariantKey] = useState(DEFAULT_LANDING_VARIANT);
  const [variantLoading, setVariantLoading] = useState(false);
  const [variantMeta, setVariantMeta] = useState({ source: '', campaign: '', geo: '' });
  const linkBack = "";
  const linkSkip = "";
  const phoneNumberStyle = { // (behåll inline pga lagg i css-laddning)
    flex: '1 0 auto',
    border: 'none',
    padding: '15px 15px 15px 15px',
    fontSize: '20px',
    fontFamily: 'Montserrat',
    textAlign: 'center',
    borderRadius: '40px',
    color: "#000",
    backgroundColor: "#fff"
  };

  const checkIfInviterExistsUrl = process.env.REACT_APP_CLOUD_FUNCTION_API_CHECKIFINVITEREXISTS;
  const resolveInvitationCodeUrl = process.env.REACT_APP_CLOUD_FUNCTION_API_RESOLVEINVITATIONCODE;
  const getLandingVariantUrl = process.env.REACT_APP_CLOUD_FUNCTION_API_GETLANDINGVARIANT;

  // --- Hjälp: kodvalidering ---
  const sanitizeCode = (v) => (v || '').toString().toUpperCase().replace(/\s+/g, '');
  const isValidInviteCode = (v) => /^[A-Z0-9]{6,12}$/.test(sanitizeCode(v));
  const toNationalDashedSE = (e164) => {
    const s = String(e164 || '');
    if (!s) return '';
    const pn = parsePhoneNumberFromString(s);
    if (!pn || !pn.isValid()) return s;

    // Få det nationella formatet, t.ex. "070 123 45 67"
    return pn.formatNational();
  };

    // 🔹 Läs attribution från sessionStorage
  const readAttribution = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY_ATTRIBUTION);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // 🔹 Wrapper för Meta Pixel events
  const trackMetaCustom = (name, params) => {
    const attr = readAttribution();
    if (!attr?.isMetaTraffic) return; // bara Meta-trafik
    if (window.fbq) {
      window.fbq('trackCustom', name, params);
    }
  };

  // 🔹 GA4 landing_page – en gång per session (Step 1 laddad)
  useEffect(() => {
    const KEY = 'h.lp'; // h.-prefixad session-flagga, matchar övriga nycklar
    try {
      if (sessionStorage.getItem(KEY)) return; // redan skickad denna session
      sessionStorage.setItem(KEY, '1');
    } catch {
      // sessionStorage blockerad – skicka ändå (kan då dubblera i sällsynta fall)
    }
    trackGA4('landing_page', { mode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Landningsvariant-lookup för /i/-koder (enbart för att välja copy-variant,
  //    ingen validering av disabled/expiresAt/maxUses/uses här).
  useEffect(() => {
    let cancelled = false;

    // Gäller bara kampanjkoder som kommer via URL. Vän-läge + manuell inmatning
    // hämtar ingen variant här (vän-läge sätts via FRIEND_LANDING_VARIANT nedan).
    if (!(mode === 'code' && codeResolvedFromParams && inviteCode)) {
      setVariantKey(DEFAULT_LANDING_VARIANT);
      setVariantMeta({ source: '', campaign: '', geo: '' });
      setVariantLoading(false);
      return;
    }

    const code = sanitizeCode(inviteCode);
    setVariantLoading(true);

    (async () => {
      try {
        const response = await fetch(getLandingVariantUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ inviteCode: code }),
        });
        const result = await response.json();
        if (cancelled) return;

        // getLandingVariant() faller tillbaka på buyer_default för okänd/saknad nyckel.
        setVariantKey(result?.landingVariant || DEFAULT_LANDING_VARIANT);
        setVariantMeta({
          source: result?.source || '',
          campaign: result?.campaign || '',
          geo: result?.geo || '',
        });
      } catch (err) {
        // Fallback-säker: sidan ska alltid kunna rendera buyer_default.
        console.log('Felmeddelande (landing variant):', err);
        if (!cancelled) {
          setVariantKey(DEFAULT_LANDING_VARIANT);
          setVariantMeta({ source: '', campaign: '', geo: '' });
        }
      } finally {
        if (!cancelled) setVariantLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, codeResolvedFromParams, inviteCode, getLandingVariantUrl]);

  // Sätt felmeddelande om länk från vän inte fungerar
  useEffect(() => {
    if (inviteLinkError) {
    setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Ogiltig länk. Be din vän skicka en ny eller <button onClick={handleResetInviter} className="underline decoration-dotted">ange inbjudningskod.</button></p>)
    setError(true)
    } else {
      setError(false)
    }
  }, [inviteLinkError])

  // --- API: verifiera inviter via telefonnummer (befintlig funktionalitet) ---
  const handleVerifyInviterByPhone = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(checkIfInviterExistsUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inviterPhoneNumber }),
      });

      const inviterExists = await response.json();
      if (inviterExists) {
        // Spara inviter och mode så nästa steg vet hur vi kom in
        sessionStorage.setItem(LOCAL_STORAGE_KEY_INVITER_PHONE_NUMBER, JSON.stringify(inviterPhoneNumber));
        sessionStorage.setItem(LOCAL_STORAGE_KEY_MODE, JSON.stringify(mode));
        // Persista vän-variant så Step 2 visar matchande copy (och inte ärver en gammal kod-variant)
        sessionStorage.setItem(SESSION_STORAGE_KEY_LANDING_VARIANT, JSON.stringify(FRIEND_LANDING_VARIANT));
        setIsLoading(false);
        navigate("../app/complete-your-invite");
      } else {
        setIsLoading(false);
        setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Inbjudan har gått ut. Be din vän om en ny.</p>);
        setError(true);
      }
    } catch (err) {
      console.log("Felmeddelande (telefon):", err);
      setIsLoading(false);
      setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Något gick fel. Försök igen.</p>);
      setError(true);
    }
  };

  // --- API: verifiera inviter via inbjudningskod (NY) ---
  const handleVerifyInviterByCode = async () => {
    const code = sanitizeCode(inviteCode);
    if (!isValidInviteCode(code)) {
      setError(true);
      // Olika felmeddelande om manuellt ifylld (uppmaning att se över kod) eller ifylld via länk (möjlighet att korrigera själv)
      if (codeResolvedFromParams) {
        setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Ogiltig kod. Vill du <button onClick={handleResetCode} className="underline decoration-dotted">fylla i kod manuellt?</button></p>);
      } else {
        setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Ogiltig kod. Använd 6–12 tecken (A–Z, 0–9).</p>);
      }
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(resolveInvitationCodeUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inviteCode: code }),
      });

      // Förväntat svar (exempel): { ok: true }
      const result = await response.json();

      if (result?.ok) {
        // Spara kod och mode så nästa steg vet hur vi kom in
        sessionStorage.setItem(LOCAL_STORAGE_KEY_INVITE_CODE, JSON.stringify(code));
        sessionStorage.setItem(LOCAL_STORAGE_KEY_MODE, JSON.stringify(mode));
        // Persista resolvd landningsvariant så Step 2 kan visa matchande copy
        sessionStorage.setItem(SESSION_STORAGE_KEY_LANDING_VARIANT, JSON.stringify(variantKey));
        setIsLoading(false);
        navigate("../app/complete-your-invite");
      } else {
        setIsLoading(false);
        const reason = result?.reason || '';
        if (reason === 'expired') {
          if (codeResolvedFromParams) {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Koden har gått ut. Vill du <button onClick={handleResetCode} className="underline decoration-dotted">fylla i kod manuellt?</button></p>);
          } else {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Koden har gått ut. Fyll i en annan eller be en vän bjuda in.</p>);
          }
        } else if (reason === 'used') {
          if (codeResolvedFromParams) {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Koden har förbrukats. Vill du <button onClick={handleResetCode} className="underline decoration-dotted">fylla i kod manuellt?</button></p>);
          } else {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Koden har förbrukats. Fyll i en annan eller be en vän bjuda in.</p>);
          }
        } else {
          if (codeResolvedFromParams) {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Ogiltig kod. Vill du <button onClick={handleResetCode} className="underline decoration-dotted">fylla i kod manuellt?</button></p>);
          } else {
            setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Ogiltig kod. Kontrollera koden eller be en vän bjuda in.</p>);
          }
        }
        setError(true);
      }
    } catch (err) {
      console.log("Felmeddelande (kod):", err);
      setIsLoading(false);
      setErrorMessage(<p className='text-center text-xs text-[#EF4444]'>Något gick fel. Försök igen.</p>);
      setError(true);
    }
  };

  // --- Reset-knappar för förifyllda värden ---
  const handleResetInviter = () => {
    setInviterResolvedFromParams(false);
    setInviterPhoneNumber('');
    setMode('code')
    setError(false);
  };

  const handleResetCode = () => {
    setCodeResolvedFromParams(false);
    setInviteCode('');
    setError(false);
  };

  // --- Submit-knappen ---
  const handleSubmit = (event) => {
    event.preventDefault();
    setError(false);

    // Meta StartRegistration – endast Meta-trafik
    trackMetaCustom('StartRegistration', {
      landingVariant: variantKey,
      source: variantMeta.source,
      campaign: variantMeta.campaign,
      geo: variantMeta.geo,
    });

    // GA4 start_registration – all trafik
    trackGA4('start_registration', {
      method: mode, // 'code' | 'friend'
      landing_variant: variantKey,
      source: variantMeta.source,
      campaign: variantMeta.campaign,
      geo: variantMeta.geo,
    });

    if (mode === 'code') {
      handleVerifyInviterByCode();
    } else {
      handleVerifyInviterByPhone();
    }
  };

  // --- Enable/disable CTA ---
  useEffect(() => {
    // mode === 'code
    if (mode === 'code') {
      setButtonDisabled(!isValidInviteCode(inviteCode));
      return;
    }
    
    // mode === 'friend'
    const hasPhone = inviterPhoneNumber && inviterPhoneNumber.trim() !== "";
    setButtonDisabled(!hasPhone);
      
  }, [mode, inviteCode, inviterPhoneNumber, inviterResolvedFromParams]);

  const openUserAgreement = () => {
    window.open('https://houtive.com/anvandarvillkor', '_blank', 'noopener,noreferrer');
  };

  const openIntegrityPolicy = () => {
    window.open('https://houtive.com/integritetspolicy', '_blank', 'noopener,noreferrer');
  };

  // Vän-läge visar buyer_demand_v1-copyn (rubrik/underrubrik/CTA/bild),
  // men behåller vän-pill och 48h-raden nedan.
  const activeVariantKey = mode === 'code' ? variantKey : FRIEND_LANDING_VARIANT;
  const activeVariant = getLandingVariant(activeVariantKey);
  // Preview-varianter får produktpreview + reducerad copy (ingen explainer/aboveCta);
  // alla andra varianter oförändrade. Nyckel som saknas här → activePreview null.
  const activePreview = VARIANT_PREVIEWS[activeVariantKey] || null;

  // Rendera aboveCta-texten med det markerade ordet (aboveCtaHighlight) i amber.
  const renderAboveCta = (variant) => {
    const { aboveCta, aboveCtaHighlight } = variant;
    if (!aboveCtaHighlight || !aboveCta.includes(aboveCtaHighlight)) {
      return aboveCta;
    }
    const [before, ...rest] = aboveCta.split(aboveCtaHighlight);
    return (
      <>
        {before}
        <span className="text-[#F59E0B] font-medium">{aboveCtaHighlight}</span>
        {rest.join(aboveCtaHighlight)}
      </>
    );
  };

  // --- Loading-gate: medan landningsvarianten hämtas för /i/-koder, undvik
  //     att fel copy blinkar till genom att visa en enkel loading-vy. ---
  if (variantLoading) {
    return (
      <div className='flex flex-1 h-[calc(100dvh)] flex-col items-center justify-center bg-[#000]'>
        <img src={AppHeaderLogo} alt='Houtive' className='max-w-[100px] mb-6' />
        <div className='w-8 h-8 rounded-full border-2 border-[#3A3A3A] border-t-white animate-spin' />
      </div>
    );
  }

  // --- UI ---
  return (
    <div className='flex flex-1 h-[calc(100dvh)] flex-col items-center justify-center bg-[#000]'>
      {/* Upper section */}
      <div className='flex flex-1 justify-between flex-col'>
        <div className='mt-3'>
          <img src={AppHeaderLogo} className='max-w-[100px]'/>
           {/* <h2 className='text-xl text-center text-[#fff]'>
             houtive
           </h2> */}
        </div>
        <Navigation
          linkBack={linkBack}
          linkSkip={linkSkip}
          showLinkBack={false}
          showLinkSkip={false}
        />
      </div>
      {/* Middle section */}
      <div className='flex flex-1 justify-center flex-col w-full max-w-[420px] px-6'>
         <div className="text-center mb-4">
          {/* Resolved from FRIEND */}
          {mode === 'friend' && (
            <div className='flex justify-center items-center w-full'>
              {inviterPhoneNumber &&
                <div className="bg-[#1A1A1A] text-[#D6D6D6] text-xs rounded-full px-3 py-1 inline-block">
                  Du har blivit inbjuden av {toNationalDashedSE(inviterPhoneNumber)}{" "}💫
                </div>}
            </div>
          )}
          {/* Resolved from FRIEND */}
          {mode === 'code' && (
            <div className='flex justify-center items-center w-full'>
              {codeResolvedFromParams && inviteCode && (
                <div className="bg-[#1A1A1A] text-[#D6D6D6] text-xs rounded-full px-3 py-1 inline-block">
                  {activeVariant.pill}
                </div>
              )}
            </div>
          )}
        </div>
        <h2 className="
          text-3xl sm:text-4xl
          font-semibold
          text-center
          leading-tight
          text-white
          mb-4
        ">
          {activeVariant.heading}
        </h2>
        <p className="
          text-center
          text-lg sm:text-xl
          font-medium
          text-white
          leading-relaxed
          mb-2
        ">
          {activeVariant.subheading}
        </p>
        {activePreview ? (
          <div className="flex flex-col items-center mb-6">
            <img
              src={activePreview.image}
              alt={activePreview.alt}
              className="w-full max-w-[240px] h-auto mx-auto mt-3"
            />
          </div>
        ) : (
          <p className="
            max-w-md
            mx-auto
            text-center
            text-xs sm:text-sm
            text-white/65
            leading-relaxed
            mb-6
          ">
            {activeVariant.explainer}
          </p>
        )}
        {/* FRIEND-MODE */}
        {mode === 'friend' && (
          <div className='flex flex-col items-center justify-end w-full'>
            {inviterPhoneNumber && (
              // "Pill" som ersätter input när värde kommer från URL
              <div className="w-full">
                <p className='text-center text-xs text-[#D6D6D6]'>
                  Registrera dig inom <span className="text-[#F59E0B] font-medium">48 timmar</span> för att säkra din plats.
                </p>
              </div>
            )}
          </div>
        )}

        {/* CODE-MODE */}
        {mode === 'code' && (
          <div className='flex flex-col items-center w-full'>
            {codeResolvedFromParams && inviteCode ? (
              // "Pill" som ersätter input när värde kommer från URL.
              // Preview-varianter renderar ingen aboveCta (reducerad copy).
              !activePreview && (
                <div className="w-full">
                  <p className='text-[#9A9A9A] text-xs text-center'>
                    {renderAboveCta(activeVariant)}
                  </p>
                </div>
              )
            ) : (
              <>
                <input
                  value={inviteCode}
                  onChange={(e) => setInviteCode(sanitizeCode(e.target.value))}
                  inputMode="latin"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Ange din inbjudningskod"
                  className="w-full placeholder-[#8A8A8A] bg-white outline-none"
                  style={phoneNumberStyle}
                />
                <p className='text-[#9A9A9A] text-xs mt-3 text-center'>
                  Koden hittar du i din inbjudan.
                </p>
                <p className='text-[#F59E0B] text-xs mt-3 text-center'>
                  Platserna per kod är begränsade - säkra din innan de tar slut.
                </p>
              </>
            )}
          </div>
        )}

      </div>
      {/* Bottom section */}
      <div className='w-full flex flex-1 flex-col justify-center items-center max-w-[420px] px-6'>
        {error && 
        <div>
          {errorMessage}
        </div>}
        <ButtonNext
          handleSubmit={handleSubmit}
          buttonDisabled={buttonDisabled}
          isLoading={isLoading}
          // error={error}
          // errorMessage={errorMessage}
          buttonText={activeVariant.cta}
        />
         <p className="mt-3 text-center text-xs text-[#D6D6D6]">
            Genom att fortsätta godkänner du Houtives{" "}
            <button
              type="button"
              onClick={openUserAgreement}
              className="text-[#9D4EDD] underline decoration-dotted underline-offset-2"
            >
              användarvillkor
            </button>{" "}
            och{" "}
            <button
              type="button"
              onClick={openIntegrityPolicy}
              className="text-[#9D4EDD] underline decoration-dotted underline-offset-2"
            >
              integritetspolicy
            </button>
            .
          </p>
      </div>
    </div>
  );
};

export default WhoInvitedMe;
